import { isDefined } from '@sgme/fp';
import { Button, HStack, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import { memo, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import CommercialMargin from '@/App/tiles/ForwardAccumulator/parts/CommercialMargin.tsx';
import Footer from '@/App/tiles/ForwardAccumulator/parts/Footer/index.tsx';
import { Header } from '@/App/tiles/ForwardAccumulator/parts/Header';
import { HedgeAmount } from '@/App/tiles/ForwardAccumulator/parts/HedgeAmount.tsx';
import { HedgePaymentDate } from '@/App/tiles/ForwardAccumulator/parts/HedgePaymentDate.tsx';
import { HedgeType } from '@/App/tiles/ForwardAccumulator/parts/HedgeType.tsx';
import Delta from '@/App/tiles/ForwardAccumulator/parts/MarketData/Delta.tsx';
import Forward from '@/App/tiles/ForwardAccumulator/parts/MarketData/Forward.tsx';
import SwapPoints from '@/App/tiles/ForwardAccumulator/parts/MarketData/SwapPoints.tsx';
import Vega from '@/App/tiles/ForwardAccumulator/parts/MarketData/Vega.tsx';
import Volatility from '@/App/tiles/ForwardAccumulator/parts/MarketData/Volatility.tsx';
import { PremiumDate } from '@/App/tiles/ForwardAccumulator/parts/PremiumDate.tsx';
import { PremiumPriceType } from '@/App/tiles/ForwardAccumulator/parts/PremiumPriceType.tsx';
import PricerView from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Pricer/PricerView';
import UnzoomedTileSchedulerView from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/UnzoomedTileSchedulerView.tsx';
import ZoomedTileSchedulerView from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/ZoomedTileSchedulerView.tsx';
import { SpotField } from '@/App/tiles/ForwardAccumulator/parts/SpotField.tsx';
import { SpotRef } from '@/App/tiles/ForwardAccumulator/parts/SpotRef.tsx';
import {
  useForwardAccumulatorSelector,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider';
import { ErrorTile } from '@/App/tiles/shared/ErrorTile.tsx';
import { ExecutionStatus } from '@/App/tiles/shared/ExecutionStatus.tsx';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { isNotDefined } from '@/components/sg-design-system/utils/index.ts';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import {
  selectDisplayMode,
  selectForwardAccumulatorProperty,
  selectForwardAccumulatorTradeCaptureError,
} from '@/store/forwardAccumulator/selectors.ts';
import { resetForwardAccumulatorThunk } from '@/store/forwardAccumulator/thunk.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabZoomedTileId, setZoomedTileIdForActiveTab } from '@/store/tabs/slice.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

import { PriceSummary } from './parts/PriceSummary';

export const Tile = () => {
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const tileId = useForwardAccumulatorTileId();

  const currencyPair = useForwardAccumulatorSelector((product) => product.properties.currencyPair);
  const status = useForwardAccumulatorSelector((product) => product.execution?.status);

  const tileWidth = useAppSelector((state) => tilesSlice.selectors.selectTileW(state, tileId));
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;
  const hasTradeCaptureError = useAppSelector((state) =>
    isDefined(selectForwardAccumulatorTradeCaptureError(state, tileId)),
  );

  const deZoom = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('ForwardAccumulator', 'DeZoom button clicked', tileId);
    dispatch(setZoomedTileIdForActiveTab({ tileId: undefined }));
  }, [sgwtWebAnalytics, tileId]);

  const handleReset = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Reset after error', tileId);
    dispatch(resetForwardAccumulatorThunk({ tileId }));
  }, [sgwtWebAnalytics, tileId]);

  const hasCurrencyPair = isDefined(currencyPair);

  useEffect(() => {
    const w = hasCurrencyPair ? 3 : 1;
    if (tileWidth !== w) {
      dispatch(tilesSlice.actions.resizeTile({ tileId, w }));
    }
  }, [hasCurrencyPair, tileId, tileWidth]);

  if (hasTradeCaptureError) {
    return <ErrorTile tileId={tileId} onReset={handleReset} />;
  }

  if (isDefined(status)) {
    return <ExecutionStatus status={status} />;
  }

  return (
    <VStack fullWidth verticalAlignment="start" className="bg-lvl1" style={{ height: isZoomed ? '100%' : 'auto' }}>
      <AutoAdvanceForm className={isZoomed ? 'd-flex flex-column h-100' : undefined}>
        {isZoomed && (
          <div>
            <Button onClick={deZoom}>
              <em className="icon">arrow_back</em>
              <FormattedMessage id="zoomModeLayout.backToWorkspace" />
            </Button>
          </div>
        )}
        {isNotDefined(currencyPair) && (
          <div className="p-8px">
            <Header />
          </div>
        )}

        {isDefined(currencyPair) && (
          <HStack
            flex="expand"
            horizontalAlignment="start"
            verticalAlignment="start"
            className={clsx('h-100', { 'border-top pe-3': isZoomed })}
          >
            <LeftColumn tileId={tileId} isZoomed={isZoomed} />
            <RightPanel tileId={tileId} isZoomed={isZoomed} />
          </HStack>
        )}
      </AutoAdvanceForm>
    </VStack>
  );
};

type ColumnProps = { tileId: string; isZoomed: boolean };

const LeftColumn = memo(function LeftColumn({ tileId, isZoomed }: ColumnProps) {
  const hedgeType = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'hedgeType'));

  return (
    <div className="border-end align-self-stretch">
      <VStack fullHeight={!isZoomed}>
        <div className="p-8px">
          <Header />
          <SpotField />
          {hedgeType !== 'Live' && <SpotRef />}
        </div>

        <PriceSummary />
        <PremiumPriceType />
        <PremiumDate />
        <HedgeType />
        {hedgeType === 'Spot' && (
          <>
            <HedgePaymentDate />
            <HedgeAmount />
          </>
        )}

        {/* Market data */}
        <Volatility />
        <Forward />
        <SwapPoints />
        <Delta />
        <Vega />

        <UsertypeGuard allow="Internal Sales">
          <CommercialMargin />
        </UsertypeGuard>

        <Footer />
      </VStack>
    </div>
  );
});

const RightPanel = memo(function RightPanel({ tileId, isZoomed }: ColumnProps) {
  const displayMode = useAppSelector((state) => selectDisplayMode(state, tileId));

  if (displayMode === 'Pricer') {
    return <PricerView />;
  }
  if (displayMode === 'Scheduler') {
    return isZoomed ? <ZoomedTileSchedulerView /> : <UnzoomedTileSchedulerView />;
  }
  return null;
});
